import { useAppContext } from "../../../hooks/appContext"
import { addPageAction } from "../../../utils/newrelic"
import {
    Messaging as MessagingActive,
    MessagingInactive,
} from "../../common/atoms/Icons/Chat"
import { UnreadBadge } from "../common/UnreadBadge/UnreadBadge"
import { useUnreadCount } from "../hooks/useUnreadCount"
import styles from "./MessagingIcon.module.scss"
import type { ConversationListData, DMUnreadData, EventRouter } from "../types"

type UnreadStyle = "dot" | "badge"

type Props = {
    dmUnreadData: DMUnreadData
    conversationListData: ConversationListData
    toggleDms: EventRouter<boolean>
    onClick: () => void
    compact?: boolean
    unreadStyle?: UnreadStyle
}

export function MessagingIcon({
    dmUnreadData,
    conversationListData,
    toggleDms,
    onClick,
    compact = false,
    unreadStyle = "badge",
}: Props) {
    const { unreadCount } = useUnreadCount({
        dmUnreadData,
        conversationListData,
    })
    const {
        context: { is_mobile, logged_in_user },
    } = useAppContext()

    const handleClick = () => {
        if (!logged_in_user) {
            // We will retain showing the login menu from MobileDmsMenu if not logged in
            return
        }

        toggleDms.fire(false)
        onClick()
        addPageAction("DMListOpened", {
            source: is_mobile ? "mobile" : "icon",
            hasUnread: unreadCount > 0,
            inboxVersion: "v2",
        })
    }

    const iconSize = compact
        ? { height: 13, width: 13 }
        : { height: 18, width: 18 }

    const Icon =
        unreadCount > 0 ? (
            <MessagingActive {...iconSize} />
        ) : (
            <MessagingInactive {...iconSize} />
        )

    return (
        <div
            className={styles.container}
            onClick={handleClick}
            data-testid="messaging-icon"
        >
            <div className={styles.imgContainer}>
                {Icon}
                {unreadStyle === "badge" ? (
                    <UnreadBadge
                        count={unreadCount}
                        variant={compact ? "compact" : undefined}
                    />
                ) : (
                    unreadCount > 0 && (
                        <div
                            className={styles.unreadDot}
                            data-testid="unread-dot"
                        />
                    )
                )}
            </div>
        </div>
    )
}
